import { useState, useCallback, useMemo } from 'react';
import {
  Button,
  VStack,
  Box,
  Icon,
  Separator,
  Grid,
  AbsoluteCenter,
} from '@chakra-ui/react';
import { PlusIcon, DocumentTextIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import SelectedContentList from '@/components/fillable-blocks/section-blocks/selected-content-list';
import ContentBlock from '@/components/fillable-blocks/section-blocks/content-block';
import { ContentType, Content } from '@/lib/types';
import CarouselEmptyState from '@/components/home/carousel-empty-state';

interface Props {
  contentRefs: string[];
  contentTypes: ContentType[];
  workspaceId: number;
  contents: Content[];
  onChange(contentRefs: string[], item?: Content): void;
}

export default function ContentSelector({
  contentRefs,
  contentTypes,
  workspaceId,
  contents,
  onChange,
}: Props) {
  const [contentType, setContentType] = useState<ContentType>();
  const contentBlockDrawer = useModalToggle();

  const hasContent = useMemo(() => contentRefs.length > 0, [contentRefs]);
  const hasContentTypes = useMemo(() => contentTypes.length > 0, [contentTypes]);

  const selectedContents = useMemo(
    () =>
      contents.filter(
        (content) => content.reference_id && contentRefs.includes(content.reference_id),
      ),
    [contents, contentRefs],
  );

  const addContent = useCallback(
    (refs: string[], item: Content) => {
      onChange(refs, item);
    },
    [onChange],
  );

  const openContentSelector = useCallback(
    (type: ContentType) => {
      setContentType(type);
      contentBlockDrawer.open();
    },
    [contentBlockDrawer],
  );

  const removeItem = useCallback(
    (referenceId: string) => {
      if (!referenceId) return;
      const updatedRefs = contentRefs.filter((id) => id !== referenceId);
      onChange(updatedRefs);
    },
    [contentRefs, onChange],
  );

  return (
    <Box mt={6}>
      <Box position='relative' py={4} w='full' mb={3}>
        <Separator borderColor='border' />
        <AbsoluteCenter bg={'bg.panel'} px={'4'}>
          Contents
        </AbsoluteCenter>
      </Box>

      {!hasContent ? (
        <Box mb={6}>
          <CarouselEmptyState
            icon={DocumentTextIcon}
            title='No content added yet'
            description={
              hasContentTypes
                ? 'Select a content type below to add content'
                : 'No content has been added to this section'
            }
          />
        </Box>
      ) : (
        <VStack gap={2} align='stretch' mb={6}>
          <SelectedContentList
            contents={selectedContents || []}
            onChange={onChange}
            removeItem={(referenceId) => removeItem(referenceId)}
          />
        </VStack>
      )}

      {hasContentTypes ? (
        <Grid
          templateColumns={{
            base: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          }}
          gap={4}
          mb={6}
        >
          {contentTypes.map((type) => (
            <Button
              key={type.id}
              variant='outline'
              size='sm'
              justifyContent='flex-start'
              textOverflow='ellipsis'
              overflow='hidden'
              whiteSpace='nowrap'
              title={`Add ${type.name} content`}
              onClick={() => openContentSelector(type)}
            >
              <Icon as={PlusIcon} w={4} h={4} mr={2} />
              {type.name}
            </Button>
          ))}
        </Grid>
      ) : (
        <CarouselEmptyState
          icon={ExclamationCircleIcon}
          iconBg='danger.subtle'
          iconColor='danger'
          title='No content types available'
          description='This workspace has no content types configured. Contact your workspace administrator.'
        />
      )}

      {workspaceId && (
        <ContentBlock
          {...contentBlockDrawer.props}
          workspaceId={workspaceId}
          contentType={contentType}
          contentRefs={contentRefs}
          showPartner={true}
          onSuccess={addContent}
        />
      )}
    </Box>
  );
}
